import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Alert,
  CircularProgress,
  Tooltip,
  Grid,
  SelectChangeEvent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CalendarMonth as CalendarIcon,
  Schedule as ScheduleIcon,
  SportsHandball as CourtIcon,
  Clear as ClearIcon,
  Print as PrintIcon,
  ContentCopy as ContentCopyIcon,
  EventAvailable as EventAvailableIcon,
  Calculate as CalculateIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { CourtBooking, Division, SPL_DIVISIONS, CourtSlot } from '../types';
import { apiRequest, API_BASE_URL } from '../config/api';
import { format, parseISO } from 'date-fns';
import { formatLeagueDayDateCustom } from '../utils/dateFormat';
import VenueSelector from '../components/VenueSelector';
import VenueBadge from '../components/VenueBadge';
import MultiDateCalendar from '../components/MultiDateCalendar';

const CourtManagement: React.FC = () => {
  const [bookings, setBookings] = useState<CourtBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [slotAssignDialog, setSlotAssignDialog] = useState(false);
  const [editingBooking, setEditingBooking] = useState<CourtBooking | null>(null);
  const [manageBookingsDialogOpen, setManageBookingsDialogOpen] = useState(false);
  const [manageBookingsDateKey, setManageBookingsDateKey] = useState<string>('');

  // Slot assignment state
  const [selectedBookingId, setSelectedBookingId] = useState<string>('');
  const [selectedCourtNumber, setSelectedCourtNumber] = useState<number>(0);
  const [selectedSlotStart, setSelectedSlotStart] = useState<string>('');
  const [selectedDivision, setSelectedDivision] = useState<Division | ''>('');

  // Single booking form state
  const [date, setDate] = useState('');
  const [courtsAvailable, setCourtsAvailable] = useState<number[]>([]);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [notes, setNotes] = useState('');
  const [venueId, setVenueId] = useState('');

  // Bulk booking form state
  const [bulkDates, setBulkDates] = useState<string[]>([]);
  const [bulkCourts, setBulkCourts] = useState<number[]>([]);
  const [bulkStartTime, setBulkStartTime] = useState('');
  const [bulkEndTime, setBulkEndTime] = useState('');
  const [bulkNotes, setBulkNotes] = useState('');
  const [bulkVenueId, setBulkVenueId] = useState('');


  // Create-league-days state
  const [createLDDialogOpen, setCreateLDDialogOpen] = useState(false);
  const [createLDDates, setCreateLDDates] = useState<string[]>([]);
  const [createLDOpenRegistration, setCreateLDOpenRegistration] = useState(true);
  const [creatingLD, setCreatingLD] = useState(false);

  // PDF export filter state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportVenueId, setExportVenueId] = useState('');
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');

  // Venue cost calculator state
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [costPricePer60, setCostPricePer60] = useState('');
  const [costFrom, setCostFrom] = useState('');
  const [costTo, setCostTo] = useState('');

  // Repeat-assignments state
  const [repeatSourceDate, setRepeatSourceDate] = useState<string | null>(null);
  const [repeatEveryWeeks, setRepeatEveryWeeks] = useState(2);
  const [repeatTimes, setRepeatTimes] = useState(0); // 0 = all future dates
  const [repeatOverwrite, setRepeatOverwrite] = useState(true);
  const [repeating, setRepeating] = useState(false);

  const AVAILABLE_COURTS = [1, 2, 3, 4, 5];

  // Get color for division chips
  const getDivisionColor = (division: Division): { backgroundColor: string; color: string } => {
    if ((division as string) === 'Trial Day') {
      return { backgroundColor: '#00897b', color: '#ffffff' }; // Teal
    }
    switch (division) {
      case 'Master':
        return { backgroundColor: '#0d47a1', color: '#ffffff' }; // Dark blue
      case 'Division 1':
        return { backgroundColor: '#1976d2', color: '#ffffff' }; // Lighter blue
      case 'Division 2':
        return { backgroundColor: '#42a5f5', color: '#ffffff' }; // Even lighter blue
      case 'Division 2A':
        return { backgroundColor: '#b5651d', color: '#ffffff' }; // Brown
      case 'Division 2B':
        return { backgroundColor: '#a0522d', color: '#ffffff' }; // Darker brown
      case 'Division 3':
        return { backgroundColor: '#90caf9', color: '#000000' }; // Lightest blue
      case 'Division 4':
        return { backgroundColor: '#bcaaa4', color: '#000000' }; // Light brown-gray
      case 'Women Division':
        return { backgroundColor: '#c2185b', color: '#ffffff' }; // Dark pink
      case 'Women Division 2':
        return { backgroundColor: '#f06292', color: '#ffffff' }; // Lighter pink
      default:
        return { backgroundColor: '#757575', color: '#ffffff' }; // Gray for others
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async (preserveScroll = false, silent = false) => {
    const scrollY = preserveScroll ? window.scrollY : 0;
    try {
      if (!silent) setLoading(true);
      const response = await apiRequest('/api/court-bookings');
      if (!response.ok) throw new Error('Failed to fetch court bookings');
      const data = await response.json();
      setBookings(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bookings');
    } finally {
      if (!silent) setLoading(false);
      if (preserveScroll) {
        requestAnimationFrame(() => window.scrollTo(0, scrollY));
      }
    }
  };

  const handleOpenDialog = (booking?: CourtBooking) => {
    if (booking) {
      setEditingBooking(booking);
      setDate(booking.date);
      setCourtsAvailable(booking.courtsAvailable);
      setStartTime(booking.startTime);
      setEndTime(booking.endTime);
      setNotes(booking.notes || '');
      setVenueId(booking.venueId || '');
    } else {
      setEditingBooking(null);
      resetForm();
    }
    setDialogOpen(true);
  };

  // Open the add-booking dialog pre-filled with a specific date
  const handleOpenDialogForDate = (prefillDate: string) => {
    setEditingBooking(null);
    resetForm();
    setDate(prefillDate);
    // A league day is in one venue, so a second slot on the same date inherits it
    setVenueId(bookings.find((b) => b.date === prefillDate && b.venueId)?.venueId || '');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingBooking(null);
    resetForm();
  };

  const resetForm = () => {
    setDate('');
    setCourtsAvailable([]);
    setStartTime('');
    setEndTime('');
    setNotes('');
    setVenueId('');
  };

  const handleSaveBooking = async () => {
    try {
      if (!date || courtsAvailable.length === 0 || !startTime || !endTime) {
        alert('Please fill in all required fields');
        return;
      }

      const bookingData = {
        date,
        courtsAvailable,
        startTime,
        endTime,
        notes: notes || undefined,
        // Empty string clears the venue on edit; create ignores it
        venueId,
      };

      let response;
      if (editingBooking) {
        response = await apiRequest(`/api/court-bookings/${editingBooking.id}`, {
          method: 'PUT',
          body: JSON.stringify(bookingData),
        });
      } else {
        response = await apiRequest('/api/court-bookings', {
          method: 'POST',
          body: JSON.stringify(bookingData),
        });
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save booking');
      }

      await fetchBookings(true);
      handleCloseDialog();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save booking');
    }
  };

  const handleBulkCreate = async () => {
    try {
      if (bulkDates.length === 0 || bulkCourts.length === 0 || !bulkStartTime || !bulkEndTime) {
        alert('Please fill in all required fields');
        return;
      }

      const bookingsToCreate = bulkDates.map(date => ({
        date,
        courtsAvailable: bulkCourts,
        startTime: bulkStartTime,
        endTime: bulkEndTime,
        notes: bulkNotes || undefined,
      }));

      const response = await apiRequest('/api/court-bookings/bulk', {
        method: 'POST',
        body: JSON.stringify({ bookings: bookingsToCreate, venueId: bulkVenueId || undefined }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create bookings');
      }

      const result = await response.json();
      alert(result.message);
      
      await fetchBookings(true);
      setBulkDialogOpen(false);
      setBulkDates([]);
      setBulkCourts([]);
      setBulkStartTime('');
      setBulkEndTime('');
      setBulkNotes('');
      setBulkVenueId('');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create bookings');
    }
  };

  const handleDeleteBooking = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;

    try {
      const response = await apiRequest(`/api/court-bookings/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete booking');
      }

      await fetchBookings(true);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete booking');
    }
  };

  const handleOpenSlotAssignDialog = (bookingId: string, courtNumber: number, slotStartTime: string, currentDivision?: Division) => {
    setSelectedBookingId(bookingId);
    setSelectedCourtNumber(courtNumber);
    setSelectedSlotStart(slotStartTime);
    setSelectedDivision(currentDivision || '');
    setSlotAssignDialog(true);
  };

  // Inline assign: called directly from the dropdown, no dialog needed
  const handleAssignSlotInline = async (bookingId: string, courtNumber: number, slotStartTime: string, division: Division | '') => {
    try {
      if (division) {
        const response = await apiRequest(`/api/court-bookings/${bookingId}/assign-slot`, {
          method: 'POST',
          body: JSON.stringify({ courtNumber, slotStartTime, division }),
        });
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to assign slot');
        }
      } else {
        const response = await apiRequest(`/api/court-bookings/${bookingId}/unassign-slot`, {
          method: 'POST',
          body: JSON.stringify({ courtNumber, slotStartTime }),
        });
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to unassign slot');
        }
      }
      await fetchBookings(true, true);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update slot');
    }
  };

  const groupList = (list: CourtBooking[]) => {
    const grouped: { [date: string]: CourtBooking[] } = {};
    list.forEach(b => { (grouped[b.date] = grouped[b.date] || []).push(b); });
    return grouped;
  };

  // Plans copying the source date's court assignments onto later dates at the chosen rhythm.
  // Courts/time slots missing on a target date are listed in `structure` so they can be booked first.
  const planRepeat = (list: CourtBooking[]) => {
    const grouped = groupList(list);
    const empty = { targets: [] as string[], assignments: [] as any[], structure: [] as any[], skipped: 0, replaced: 0 };
    if (!repeatSourceDate) return empty;

    const base = parseISO(repeatSourceDate.slice(0, 10)).getTime();
    let targets = Object.keys(grouped).filter(d => {
      const days = Math.round((parseISO(d.slice(0, 10)).getTime() - base) / 86400000);
      return days > 0 && days % (repeatEveryWeeks * 7) === 0;
    }).sort();
    if (repeatTimes > 0) targets = targets.slice(0, repeatTimes);

    const source: { court: number; slotStartTime: string; slotEndTime: string; division: Division }[] = [];
    (grouped[repeatSourceDate] || []).forEach(b =>
      Object.entries(b.courtSlots || {}).forEach(([court, slots]) =>
        slots.forEach(s => {
          if (s.division) source.push({ court: Number(court), slotStartTime: s.slotStartTime, slotEndTime: s.slotEndTime, division: s.division as Division });
        })
      )
    );

    const assignments: any[] = [];
    const missing = new Map<string, { date: string; startTime: string; endTime: string; courts: Set<number> }>();
    let skipped = 0;
    let replaced = 0;
    targets.forEach(date => {
      const dateBookings = grouped[date] || [];
      source.forEach(src => {
        const booking = dateBookings.find(b =>
          b.courtsAvailable.includes(src.court) && b.startTime <= src.slotStartTime && b.endTime >= src.slotEndTime
        );
        if (!booking) {
          const key = `${date}|${src.slotStartTime}|${src.slotEndTime}`;
          const entry = missing.get(key) || { date, startTime: src.slotStartTime, endTime: src.slotEndTime, courts: new Set<number>() };
          entry.courts.add(src.court);
          missing.set(key, entry);
          assignments.push({ ...src, date, bookingId: null });
          return;
        }
        const current = booking.courtSlots?.[src.court]?.find(s => s.slotStartTime === src.slotStartTime)?.division;
        if (current === src.division) return;
        if (current) {
          if (!repeatOverwrite) { skipped++; return; }
          replaced++;
        }
        assignments.push({ ...src, date, bookingId: booking.id });
      });
    });

    const structure = Array.from(missing.values()).map(m => {
      const dateBookings = grouped[m.date] || [];
      const exact = dateBookings.find(b => b.startTime === m.startTime && b.endTime === m.endTime);
      return {
        date: m.date,
        startTime: m.startTime,
        endTime: m.endTime,
        courts: Array.from(m.courts),
        existingBookingId: exact?.id || null,
        existingCourts: exact?.courtsAvailable || [],
        venueId: dateBookings.find(b => b.venueId)?.venueId || '',
      };
    });
    return { targets, assignments, structure, skipped, replaced };
  };

  const handleRepeatAssignments = async () => {
    const plan = planRepeat(bookings);
    if (plan.assignments.length === 0) {
      alert('Nothing to copy: the target dates already match.');
      return;
    }
    setRepeating(true);
    try {
      // 1) Book any missing courts / time slots on the target dates
      for (const s of plan.structure) {
        const response = s.existingBookingId
          ? await apiRequest(`/api/court-bookings/${s.existingBookingId}`, {
              method: 'PUT',
              body: JSON.stringify({ courtsAvailable: Array.from(new Set([...s.existingCourts, ...s.courts])) }),
            })
          : await apiRequest('/api/court-bookings', {
              method: 'POST',
              body: JSON.stringify({
                date: s.date, courtsAvailable: s.courts, startTime: s.startTime, endTime: s.endTime,
                ...(s.venueId ? { venueId: s.venueId } : {}),
              }),
            });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error || `Failed to book courts on ${s.date}`);
        }
      }

      // 2) Re-plan against the fresh bookings so every assignment has a booking, then assign
      let fresh = bookings;
      if (plan.structure.length > 0) {
        const response = await apiRequest('/api/court-bookings');
        if (!response.ok) throw new Error('Failed to reload court bookings');
        fresh = await response.json();
      }
      const finalPlan = planRepeat(fresh);
      const autoBookedKeys = new Set(
        plan.assignments.filter(a => !a.bookingId).map(a => `${a.date}|${a.court}|${a.slotStartTime}`)
      );
      const results = await Promise.allSettled(finalPlan.assignments.filter(a => a.bookingId).map(async (a) => {
        const response = await apiRequest(`/api/court-bookings/${a.bookingId}/assign-slot`, {
          method: 'POST',
          body: JSON.stringify({
            courtNumber: a.court,
            slotStartTime: a.slotStartTime,
            division: a.division,
            ...(autoBookedKeys.has(`${a.date}|${a.court}|${a.slotStartTime}`) ? { autoBooked: true } : {}),
          }),
        });
        if (!response.ok) throw new Error('failed');
      }));
      const failed = results.filter(r => r.status === 'rejected').length;
      await fetchBookings(true, true);
      setRepeatSourceDate(null);
      if (failed > 0) alert(`${results.length - failed} slots updated, ${failed} failed.`);
    } catch (err) {
      await fetchBookings(true, true);
      alert(err instanceof Error ? err.message : 'Failed to copy assignments');
    } finally {
      setRepeating(false);
    }
  };

  // ---- Create league days from court assignments ----
  // One league day per (date, division, time slot); the number of courts assigned
  // determines the player limit (4 players per court). "Trial Day" is not a division,
  // so it becomes a trial league day in the "unassigned" division.
  const planLeagueDays = (dates: string[]) => {
    const grouped = groupBookingsByDate();
    const items: {
      date: string; assignment: string; isTrial: boolean; courts: number[];
      slotStartTime: string; slotEndTime: string; venueId: string; alreadyCreated: boolean;
      slots: { bookingId: string; court: number; slotStartTime: string; division: string }[];
    }[] = [];
    [...dates].sort().forEach(date => {
      const dateBookings = grouped[date] || [];
      const venueId = dateBookings.find(b => b.venueId)?.venueId || '';
      const groups = new Map<string, typeof items[number]>();
      dateBookings.forEach(b =>
        Object.entries(b.courtSlots || {}).forEach(([court, slots]) =>
          slots.forEach(s => {
            if (!s.division) return;
            const key = `${s.division}|${s.slotStartTime}|${s.slotEndTime}`;
            const g = groups.get(key) || {
              date, assignment: s.division as string, isTrial: (s.division as string) === 'Trial Day',
              courts: [], slotStartTime: s.slotStartTime, slotEndTime: s.slotEndTime, venueId,
              alreadyCreated: true, slots: [],
            };
            g.courts.push(Number(court));
            g.slots.push({ bookingId: b.id, court: Number(court), slotStartTime: s.slotStartTime, division: s.division as string });
            if (!s.leagueDayId) g.alreadyCreated = false;
            groups.set(key, g);
          })
        )
      );
      Array.from(groups.values())
        .sort((a, b) => a.slotStartTime.localeCompare(b.slotStartTime) || a.assignment.localeCompare(b.assignment))
        .forEach(g => { g.courts.sort((x, y) => x - y); items.push(g); });
    });
    return items;
  };

  const handleCreateLeagueDays = async () => {
    const todo = planLeagueDays(createLDDates).filter(i => !i.alreadyCreated);
    if (todo.length === 0) return;
    setCreatingLD(true);
    const failures: string[] = [];
    let created = 0;
    try {
      for (const item of todo) {
        const label = `${item.date.slice(0, 10)} ${item.assignment} ${item.slotStartTime}`;
        try {
          const res = await apiRequest('/api/league-days', {
            method: 'POST',
            body: JSON.stringify({
              division: item.isTrial ? 'unassigned' : item.assignment,
              isDraft: true,
              isTrial: item.isTrial,
              scoringFormat: item.isTrial ? 'points' : 'games',
              ...(item.isTrial ? { name: 'Trial Day' } : {}),
              courtCount: item.courts.length,
              date: `${item.date.slice(0, 10)}T00:00:00.000Z`,
              time: item.slotStartTime,
              startTime: item.slotStartTime,
              endTime: item.slotEndTime,
              availableCourts: item.courts.map(String),
              assignedCourt: item.courts.join(', '),
              ...(item.venueId ? { venueId: item.venueId } : {}),
            }),
          });
          if (!res.ok) throw new Error('create');
          const leagueDay = await res.json();

          if (createLDOpenRegistration) {
            const openRes = await apiRequest(`/api/league-days/${leagueDay.id}/open-registration`, {
              method: 'PUT',
              body: JSON.stringify({ playerLimit: item.courts.length * 4 }),
            });
            if (!openRes.ok) throw new Error('open');
          }

          // Link the court slots to the new league day (also prevents creating it twice)
          await Promise.all(item.slots.map(s =>
            apiRequest(`/api/court-bookings/${s.bookingId}/assign-slot`, {
              method: 'POST',
              body: JSON.stringify({ courtNumber: s.court, slotStartTime: s.slotStartTime, division: s.division, leagueDayId: leagueDay.id }),
            })
          ));
          created++;
        } catch {
          failures.push(label);
        }
      }
      await fetchBookings(true, true);
      setCreateLDDialogOpen(false);
      if (failures.length > 0) {
        alert(`${created} league day(s) created. Failed: ${failures.join('; ')}`);
      }
    } finally {
      setCreatingLD(false);
    }
  };

  const handleAssignSlot = async () => {
    if (!selectedDivision) {
      alert('Please select a division');
      return;
    }

    try {
      const response = await apiRequest(`/api/court-bookings/${selectedBookingId}/assign-slot`, {
        method: 'POST',
        body: JSON.stringify({
          courtNumber: selectedCourtNumber,
          slotStartTime: selectedSlotStart,
          division: selectedDivision,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to assign slot');
      }

      await fetchBookings(true);
      setSlotAssignDialog(false);
      setSelectedBookingId('');
      setSelectedCourtNumber(0);
      setSelectedSlotStart('');
      setSelectedDivision('');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to assign slot');
    }
  };

  const handleUnassignSlot = async (bookingId: string, courtNumber: number, slotStartTime: string) => {
    if (!window.confirm('Unassign this slot?')) return;

    try {
      const response = await apiRequest(`/api/court-bookings/${bookingId}/unassign-slot`, {
        method: 'POST',
        body: JSON.stringify({
          courtNumber,
          slotStartTime,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to unassign slot');
      }

      await fetchBookings(true);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to unassign slot');
    }
  };

  // Venues that appear in existing bookings (used for the export filter)
  const getExportVenues = () => {
    const map = new Map<string, string>();
    bookings.forEach(b => { if (b.venueId) map.set(b.venueId, b.venueName || b.venueId); });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  };

  const toMinutes = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
  };

  // Booked time per venue: every booking counts its duration once per court it reserves
  const getVenueCostRows = () => {
    const rows = new Map<string, { name: string; days: Set<string>; bookings: number; courtMinutes: number; items: { date: string; startTime: string; endTime: string; courts: number; minutes: number; courtMinutes: number }[] }>();
    bookings.forEach(b => {
      const d = b.date.slice(0, 10);
      if (costFrom && d < costFrom) return;
      if (costTo && d > costTo) return;
      const key = b.venueId || '__none__';
      const row = rows.get(key) || { name: b.venueName || (b.venueId ? b.venueId : 'No venue set'), days: new Set<string>(), bookings: 0, courtMinutes: 0, items: [] };
      // Only slots with a division assigned are counted, grouped by slot window (e.g. 10:30-12:00 × 4 courts).
      // Legacy bookings without any slot data fall back to the whole booking window.
      const windows = new Map<string, { startTime: string; endTime: string; courts: number }>();
      if (b.courtSlots) {
        Object.values(b.courtSlots).forEach(slots => (slots || []).forEach(s => {
          if (!s.division || s.division === 'unassigned') return;
          const wKey = `${s.slotStartTime}|${s.slotEndTime}`;
          const w = windows.get(wKey) || { startTime: s.slotStartTime, endTime: s.slotEndTime, courts: 0 };
          w.courts++;
          windows.set(wKey, w);
        }));
      } else {
        windows.set('all', { startTime: b.startTime, endTime: b.endTime, courts: b.courtsAvailable.length });
      }
      if (windows.size === 0) return;
      row.days.add(d);
      row.bookings++;
      windows.forEach(w => {
        const minutes = Math.max(0, toMinutes(w.endTime) - toMinutes(w.startTime));
        row.courtMinutes += minutes * w.courts;
        row.items.push({ date: d, startTime: w.startTime, endTime: w.endTime, courts: w.courts, minutes, courtMinutes: minutes * w.courts });
      });
      rows.set(key, row);
    });
    rows.forEach(r => r.items.sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime)));
    return Array.from(rows.values()).sort((a, b) => a.name.localeCompare(b.name));
  };

  // Group a venue's bookings by time window and court count, e.g. "09:00 - 12:00 · 2 courts × 5 days"
  const groupVenueItems = (items: ReturnType<typeof getVenueCostRows>[number]['items']) => {
    const groups = new Map<string, { startTime: string; endTime: string; courts: number; days: number; courtMinutes: number }>();
    items.forEach(i => {
      const key = `${i.startTime}|${i.endTime}|${i.courts}`;
      const g = groups.get(key) || { startTime: i.startTime, endTime: i.endTime, courts: i.courts, days: 0, courtMinutes: 0 };
      g.days++;
      g.courtMinutes += i.courtMinutes;
      groups.set(key, g);
    });
    return Array.from(groups.values()).sort((a, b) => a.startTime.localeCompare(b.startTime) || a.courts - b.courts);
  };

  const formatHours = (minutes: number) => `${Math.floor(minutes / 60)}h${minutes % 60 ? ` ${minutes % 60}m` : ''}`;

  const getExportBookings = () =>
    bookings.filter(b => {
      const d = b.date.slice(0, 10);
      if (exportFrom && d < exportFrom) return false;
      if (exportTo && d > exportTo) return false;
      if (exportVenueId === '__none__') return !b.venueId;
      if (exportVenueId && b.venueId !== exportVenueId) return false;
      return true;
    });

  const handleExportPDF = () => {
    const filtered = getExportBookings();
    if (filtered.length === 0) {
      alert('No court bookings match the selected venue and period.');
      return;
    }
    setExportDialogOpen(false);
    const groupedBookings: { [date: string]: CourtBooking[] } = {};
    filtered.forEach(b => { (groupedBookings[b.date] = groupedBookings[b.date] || []).push(b); });

    // Build HTML content
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Court Schedule Export</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 15mm;
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              background: white;
              color: #333;
              font-size: 12px;
            }
            .page {
              page-break-after: always;
              padding: 20px;
            }
            .page:last-child {
              page-break-after: auto;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              padding-bottom: 15px;
              border-bottom: 3px solid #1976d2;
            }
            .header h1 {
              color: #1976d2;
              font-size: 28px;
              margin-bottom: 8px;
            }
            .header .date {
              font-size: 20px;
              font-weight: bold;
              color: #333;
              margin-bottom: 5px;
            }
            .header .time-range {
              font-size: 14px;
              color: #666;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 30px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            th {
              background-color: #1976d2;
              color: white;
              padding: 12px 8px;
              text-align: center;
              font-weight: bold;
              font-size: 14px;
              border: 1px solid #1565c0;
            }
            td {
              padding: 10px 8px;
              text-align: center;
              border: 1px solid #ddd;
              font-size: 13px;
            }
            tr:nth-child(even) {
              background-color: #f8f9fa;
            }
            .time-slot {
              font-weight: bold;
              background-color: #e3f2fd;
              color: #1976d2;
            }
            .division {
              display: inline-block;
              padding: 6px 12px;
              color: white;
              border-radius: 12px;
              font-weight: bold;
              font-size: 12px;
            }
            .division-master {
              background-color: #0d47a1;
            }
            .division-1 {
              background-color: #1976d2;
            }
            .division-2 {
              background-color: #42a5f5;
            }
            .division-2a {
              background-color: #0288d1;
            }
            .division-2b {
              background-color: #0097a7;
            }
            .division-3 {
              background-color: #90caf9;
              color: #000000;
            }
            .division-4 {
              background-color: #b0bec5;
              color: #000000;
            }
            .division-women {
              background-color: #c2185b;
            }
            .division-women-2 {
              background-color: #f06292;
            }
            .division-trial {
              background-color: #00897b;
            }
            .division-other {
              background-color: #757575;
            }
            .na-cell {
              background-color: #fafafa;
              color: #bbb;
              font-style: italic;
            }
            .empty-cell {
              color: #999;
              font-style: italic;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              color: #666;
              font-size: 11px;
              padding-top: 10px;
              border-top: 1px solid #ddd;
            }
            @media print {
              body {
                print-color-adjust: exact;
                -webkit-print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          ${Object.entries(groupedBookings).sort().map(([date, dateBookings]) => {
            // Collect all unique courts from all bookings on this date
            const allCourts = Array.from(
              new Set(dateBookings.flatMap(b => b.courtsAvailable))
            ).sort((a, b) => a - b);

            // Collect all unique time slots from all bookings on this date
            const allSlots: { slotStartTime: string; slotEndTime: string }[] = [];
            const slotSet = new Set<string>();
            dateBookings.forEach(booking => {
              let foundAnySlot = false;
              if (booking.courtSlots) {
                booking.courtsAvailable.forEach(courtNum => {
                  const slots = booking.courtSlots![courtNum] || [];
                  slots.forEach(slot => {
                    const key = `${slot.slotStartTime}-${slot.slotEndTime}`;
                    if (!slotSet.has(key)) {
                      slotSet.add(key);
                      allSlots.push({ slotStartTime: slot.slotStartTime, slotEndTime: slot.slotEndTime });
                    }
                    foundAnySlot = true;
                  });
                });
              }
              if (!foundAnySlot) {
                const key = `${booking.startTime}-${booking.endTime}`;
                if (!slotSet.has(key)) {
                  slotSet.add(key);
                  allSlots.push({ slotStartTime: booking.startTime, slotEndTime: booking.endTime });
                }
              }
            });

            // Sort slots by start time
            allSlots.sort((a, b) => a.slotStartTime.localeCompare(b.slotStartTime));

            // Find the time range for the header
            const times = dateBookings.flatMap(b => [b.startTime, b.endTime]).sort();
            const minStartTime = times[0];
            const maxEndTime = times[times.length - 1];

            return `
              <div class="page">
                <div class="header">
                  <h1>Court Schedule</h1>
                  <div class="date">${formatLeagueDayDateCustom(date, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  }, 'en-GB')}</div>
                  <div class="time-range">${minStartTime} - ${maxEndTime}</div>
                  ${dateBookings.find(b => b.venueName) ? `<div class="time-range">📍 ${dateBookings.find(b => b.venueName)!.venueName}${dateBookings.find(b => b.venueName)!.venueAddress ? ` — ${dateBookings.find(b => b.venueName)!.venueAddress}` : ''}</div>` : ''}
                </div>
                
                <table>
                  <thead>
                    <tr>
                      <th>Time Slot</th>
                      ${allCourts.map(courtNum => `<th>Court ${courtNum}</th>`).join('')}
                    </tr>
                  </thead>
                  <tbody>
                    ${allSlots.map(slot => {
                      return `
                        <tr>
                          <td class="time-slot">${slot.slotStartTime} - ${slot.slotEndTime}</td>
                          ${allCourts.map(courtNum => {
                            // Find the booking that has this court and covers this time slot
                            const relevantBooking = dateBookings.find(booking =>
                              booking.courtsAvailable.includes(courtNum) &&
                              booking.startTime <= slot.slotStartTime &&
                              booking.endTime >= slot.slotEndTime &&
                              booking.courtSlots?.[courtNum]?.some(s =>
                                s.slotStartTime === slot.slotStartTime && s.slotEndTime === slot.slotEndTime
                              )
                            );

                            if (!relevantBooking) {
                              return `<td class="na-cell">N/A</td>`;
                            }

                            const courtSlotData = relevantBooking.courtSlots![courtNum]?.find(s =>
                              s.slotStartTime === slot.slotStartTime && s.slotEndTime === slot.slotEndTime
                            );

                            if (courtSlotData?.division) {
                              const divisionClass = 
                                courtSlotData.division === 'Master' ? 'division-master' :
                                courtSlotData.division === 'Division 1' ? 'division-1' :
                                courtSlotData.division === 'Division 2' ? 'division-2' :
                                courtSlotData.division === 'Division 2A' ? 'division-2a' :
                                courtSlotData.division === 'Division 2B' ? 'division-2b' :
                                courtSlotData.division === 'Division 3' ? 'division-3' :
                                courtSlotData.division === 'Division 4' ? 'division-4' :
                                courtSlotData.division === 'Women Division' ? 'division-women' :
                                courtSlotData.division === 'Women Division 2' ? 'division-women-2' :
                                (courtSlotData.division as string) === 'Trial Day' ? 'division-trial' :
                                'division-other';
                              return `<td><span class="division ${divisionClass}">${courtSlotData.division}</span></td>`;
                            } else {
                              return `<td class="empty-cell">Not Assigned</td>`;
                            }
                          }).join('')}
                        </tr>
                      `;
                    }).join('')}
                  </tbody>
                </table>

                <div class="footer">
                  Generated on ${new Date().toLocaleString('en-GB')}
                </div>
              </div>
            `;
          }).join('')}
        </body>
      </html>
    `;

    // Open print dialog
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    }
  };

  // Group bookings by date and merge overlapping time ranges
  const groupBookingsByDate = () => {
    const grouped: { [date: string]: CourtBooking[] } = {};
    bookings.forEach((booking) => {
      if (!grouped[booking.date]) {
        grouped[booking.date] = [];
      }
      grouped[booking.date].push(booking);
    });
    return grouped;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const groupedBookings = groupBookingsByDate();

  return (
    <Box>
      <Box 
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3
        }}
      >
        <Typography variant="h4">Court Management</Typography>
        <Box 
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            width: { xs: "100%", sm: "auto" }
          }}
        >
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => setExportDialogOpen(true)}
            disabled={bookings.length === 0}
            size="small"
          >
            Export PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<CalculateIcon />}
            onClick={() => setCostDialogOpen(true)}
            disabled={bookings.length === 0}
            size="small"
          >
            Venue Costs
          </Button>
          <Button
            variant="outlined"
            startIcon={<EventAvailableIcon />}
            onClick={() => { setCreateLDDates([]); setCreateLDDialogOpen(true); }}
            disabled={bookings.length === 0}
            size="small"
          >
            Create League Days
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setBulkDialogOpen(true)}
            size="small"
          >
            Bulk Add
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            size="small"
          >
            Add Booking
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {bookings.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            No court bookings found. Click "Add Booking" or "Bulk Add" to create bookings.
          </Typography>
        </Paper>
      ) : (
        <Box>
          {(() => {
            const today = new Date().toISOString().split('T')[0];
            const allEntries = Object.entries(groupedBookings).sort();
            const upcomingEntries = allEntries.filter(([d]) => d.slice(0, 10) >= today);
            const pastEntries = [...allEntries.filter(([d]) => d.slice(0, 10) < today)].reverse();

            const renderDateCard = ([date, dateBookings]: [string, CourtBooking[]]) => {
            // Collect all unique courts from all bookings on this date
            const allCourts = Array.from(
              new Set(dateBookings.flatMap(b => b.courtsAvailable))
            ).sort((a, b) => a - b);

            // Collect all unique time slots from all bookings on this date
            const allSlots: { slotStartTime: string; slotEndTime: string }[] = [];
            const slotSet = new Set<string>();
            dateBookings.forEach(booking => {
              let foundAnySlot = false;
              if (booking.courtSlots) {
                booking.courtsAvailable.forEach(courtNum => {
                  const slots = booking.courtSlots![courtNum] || [];
                  slots.forEach(slot => {
                    const key = `${slot.slotStartTime}-${slot.slotEndTime}`;
                    if (!slotSet.has(key)) {
                      slotSet.add(key);
                      allSlots.push({ slotStartTime: slot.slotStartTime, slotEndTime: slot.slotEndTime });
                    }
                    foundAnySlot = true;
                  });
                });
              }
              if (!foundAnySlot) {
                const key = `${booking.startTime}-${booking.endTime}`;
                if (!slotSet.has(key)) {
                  slotSet.add(key);
                  allSlots.push({ slotStartTime: booking.startTime, slotEndTime: booking.endTime });
                }
              }
            });

            // Sort slots by start time
            allSlots.sort((a, b) => a.slotStartTime.localeCompare(b.slotStartTime));

            // Find the time range for the card header
            const times = dateBookings.flatMap(b => [b.startTime, b.endTime]).sort();
            const minStartTime = times[0];
            const maxEndTime = times[times.length - 1];

            return (
              <Card key={date} sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box>
                      <Typography variant="h6">
                        <CalendarIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                        {format(parseISO(date), 'EEEE, MMMM dd, yyyy')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        <ScheduleIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                        {minStartTime} - {maxEndTime}
                      </Typography>
                      {(() => {
                        const venued = dateBookings.find((b) => b.venueName);
                        return venued ? (
                          <VenueBadge venueName={venued.venueName} venueAddress={venued.venueAddress} variant="body2" />
                        ) : (
                          <Typography variant="body2" color="warning.main">📍 No venue set</Typography>
                        );
                      })()}
                      {dateBookings.length > 1 && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                          ({dateBookings.length} bookings merged)
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Tooltip title="Add another time slot on this date">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialogForDate(date)}
                          color="success"
                          sx={{ mr: 0.5 }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Create league days (open for registration) from these court assignments">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => { setCreateLDDates([date]); setCreateLDDialogOpen(true); }}
                          sx={{ mr: 0.5 }}
                        >
                          <EventAvailableIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy these court assignments to future league days every N weeks">
                        <IconButton
                          size="small"
                          onClick={() => setRepeatSourceDate(date)}
                          sx={{ mr: 0.5 }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Manage entries for this date">
                        <IconButton
                          size="small"
                          onClick={() => { setManageBookingsDateKey(date); setManageBookingsDialogOpen(true); }}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Merged Slot Grid */}
                  <TableContainer>
                    <Table size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>Time Slot</TableCell>
                          {allCourts.map((courtNum) => (
                            <TableCell
                              key={courtNum}
                              align="center"
                              sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}
                            >
                              Court {courtNum}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allSlots.length > 0 ? (
                          allSlots.map((slot, slotIndex) => (
                            <TableRow key={slotIndex}>
                              <TableCell sx={{ fontWeight: 'bold' }}>
                                {slot.slotStartTime} - {slot.slotEndTime}
                              </TableCell>
                              {allCourts.map((courtNum) => {
                                // Find the booking that has this court and covers this time slot
                                const relevantBooking = dateBookings.find(booking => {
                                  if (!booking.courtsAvailable.includes(courtNum)) return false;
                                  // Only consider bookings whose own time range covers this slot
                                  if (booking.startTime > slot.slotStartTime || booking.endTime < slot.slotEndTime) return false;
                                  const courtSlotList = booking.courtSlots?.[courtNum];
                                  if (courtSlotList && courtSlotList.length > 0) {
                                    return courtSlotList.some(s =>
                                      s.slotStartTime === slot.slotStartTime && s.slotEndTime === slot.slotEndTime
                                    );
                                  }
                                  // Fallback: bookings with no generated courtSlots matched by exact time
                                  return booking.startTime === slot.slotStartTime && booking.endTime === slot.slotEndTime;
                                });

                                if (!relevantBooking) {
                                  // This court is not available for this time slot
                                  return (
                                    <TableCell
                                      key={courtNum}
                                      align="center"
                                      sx={{ bgcolor: 'action.disabledBackground', color: 'text.disabled' }}
                                    >
                                      N/A
                                    </TableCell>
                                  );
                                }

                                // Find the slot data for this court and time slot
                                const courtSlotData = relevantBooking.courtSlots![courtNum]?.find(s =>
                                  s.slotStartTime === slot.slotStartTime && s.slotEndTime === slot.slotEndTime
                                );

                                return (
                                  <TableCell
                                    key={courtNum}
                                    align="center"
                                    sx={{ py: 0.5, px: 1 }}
                                  >
                                    <Tooltip title={courtSlotData?.autoBooked ? 'Booked automatically by "copy every N weeks"' : ''}>
                                    <Select
                                      size="small"
                                      displayEmpty
                                      value={courtSlotData?.division || ''}
                                      onChange={(e) => handleAssignSlotInline(
                                        relevantBooking.id,
                                        courtNum,
                                        slot.slotStartTime,
                                        e.target.value as Division | ''
                                      )}
                                      onClick={(e) => e.stopPropagation()}
                                      sx={{
                                        fontSize: '0.78rem',
                                        minWidth: 120,
                                        ...(courtSlotData?.division ? getDivisionColor(courtSlotData.division as Division) : {}),
                                        '& .MuiOutlinedInput-notchedOutline': {
                                          borderColor: courtSlotData?.division
                                            ? getDivisionColor(courtSlotData.division as Division).backgroundColor
                                            : undefined,
                                        },
                                        ...(courtSlotData?.autoBooked ? {
                                          outline: '3px solid #ff9800',
                                          outlineOffset: 2,
                                        } : {}),
                                        '& .MuiSvgIcon-root': {
                                          color: courtSlotData?.division
                                            ? getDivisionColor(courtSlotData.division as Division).color
                                            : undefined,
                                        },
                                      }}
                                    >
                                      <MenuItem value=""><em>— unassigned —</em></MenuItem>
                                      {[...SPL_DIVISIONS.filter(d => d !== 'Test' && d !== 'unassigned'), 'Trial Day' as Division].map((division) => {
                                        const colors = getDivisionColor(division as Division);
                                        return (
                                          <MenuItem
                                            key={division}
                                            value={division}
                                            sx={{
                                              backgroundColor: colors.backgroundColor,
                                              color: colors.color,
                                              fontWeight: 600,
                                              '&:hover': { filter: 'brightness(1.15)', backgroundColor: colors.backgroundColor },
                                              '&.Mui-selected': {
                                                backgroundColor: colors.backgroundColor,
                                                color: colors.color,
                                                '&:hover': { filter: 'brightness(1.15)', backgroundColor: colors.backgroundColor },
                                              },
                                            }}
                                          >
                                            {division}
                                          </MenuItem>
                                        );
                                      })}
                                    </Select>
                                    </Tooltip>
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={allCourts.length + 1} align="center">
                              No slots available
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            );
            };

            return (
              <>
                {/* Upcoming dates — nearest first */}
                {upcomingEntries.length === 0 && (
                  <Paper sx={{ p: 3, textAlign: 'center', mb: 2 }}>
                    <Typography color="text.secondary">No upcoming court bookings.</Typography>
                  </Paper>
                )}
                {upcomingEntries.map(renderDateCard)}

                {/* Past dates — most recent first, visually separated */}
                {pastEntries.length > 0 && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4, mb: 2 }}>
                      <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                      <Typography variant="subtitle2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        Past Bookings ({pastEntries.length})
                      </Typography>
                      <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                    </Box>
                    <Box sx={{ opacity: 0.65 }}>
                      {pastEntries.map(renderDateCard)}
                    </Box>
                  </>
                )}
              </>
            );
          })()}
        </Box>
      )}

      {/* Create League Days Dialog */}
      <Dialog open={createLDDialogOpen} onClose={() => setCreateLDDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create League Days from Court Assignments</DialogTitle>
        <DialogContent>
          {(() => {
            const today = new Date().toISOString().split('T')[0];
            const dateKeys = Object.keys(groupBookingsByDate()).filter(d => d.slice(0, 10) >= today).sort();
            const plan = planLeagueDays(createLDDates);
            const toCreate = plan.filter(i => !i.alreadyCreated);
            return (
              <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  One league day is created per division and time slot in the active season. The number of assigned
                  courts sets the player limit (4 players per court). Trial Day slots become trial league days in the
                  "unassigned" division.
                </Typography>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ mr: 1 }}>Dates ({createLDDates.length} selected)</Typography>
                    <Button size="small" onClick={() => setCreateLDDates(dateKeys)}>All</Button>
                    <Button size="small" color="inherit" onClick={() => setCreateLDDates([])}>Clear</Button>
                  </Box>
                  <Box sx={{ maxHeight: 180, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    {dateKeys.map(d => (
                      <Box
                        key={d}
                        onClick={() => setCreateLDDates(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d])}
                        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      >
                        <Checkbox size="small" checked={createLDDates.includes(d)} />
                        <Typography variant="body2">{format(parseISO(d.slice(0, 10)), 'EEEE, MMMM dd, yyyy')}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    size="small"
                    checked={createLDOpenRegistration}
                    onChange={(e) => setCreateLDOpenRegistration(e.target.checked)}
                    sx={{ pl: 0 }}
                  />
                  <Typography variant="body2">Open registration so players can sign up right away</Typography>
                </Box>
                {plan.length > 0 && (
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 220, overflowY: 'auto' }}>
                    {plan.map((i, idx) => (
                      <Box
                        key={idx}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', opacity: i.alreadyCreated ? 0.5 : 1 }}
                      >
                        <Chip
                          size="small"
                          label={i.isTrial ? 'Trial Day (unassigned)' : i.assignment}
                          sx={getDivisionColor(i.assignment as Division)}
                        />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {format(parseISO(i.date.slice(0, 10)), 'MMM dd')} · {i.slotStartTime}-{i.slotEndTime} · {i.courts.length} court{i.courts.length !== 1 ? 's' : ''} ({i.courts.join(', ')}) · {i.courts.length * 4} players
                        </Typography>
                        {i.alreadyCreated && <Chip size="small" variant="outlined" label="Already created" />}
                      </Box>
                    ))}
                  </Box>
                )}
                <Alert severity={toCreate.length > 0 ? 'info' : 'warning'}>
                  {createLDDates.length === 0
                    ? 'Select at least one date.'
                    : plan.length === 0
                      ? 'No divisions are assigned to courts on the selected dates.'
                      : `${toCreate.length} league day${toCreate.length !== 1 ? 's' : ''} will be created.`}
                </Alert>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateLDDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateLeagueDays}
            disabled={creatingLD || planLeagueDays(createLDDates).filter(i => !i.alreadyCreated).length === 0}
          >
            {creatingLD ? 'Creating…' : 'Create League Days'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Venue Costs Dialog */}
      <Dialog open={costDialogOpen} onClose={() => setCostDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Booked Time &amp; Cost per Venue</DialogTitle>
        <DialogContent>
          {(() => {
            const rows = getVenueCostRows();
            const price = parseFloat(costPricePer60.replace(',', '.')) || 0;
            const totalMinutes = rows.reduce((n, r) => n + r.courtMinutes, 0);
            const cost = (minutes: number) => ((minutes / 60) * price).toFixed(2);
            return (
              <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Price per 60 minutes (per court)"
                  type="number"
                  size="small"
                  fullWidth
                  value={costPricePer60}
                  onChange={(e) => setCostPricePer60(e.target.value)}
                  inputProps={{ min: 0, step: 'any' }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="From"
                    type="date"
                    size="small"
                    fullWidth
                    value={costFrom}
                    onChange={(e) => setCostFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="To"
                    type="date"
                    size="small"
                    fullWidth
                    value={costTo}
                    onChange={(e) => setCostTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Venue</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Days</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Court time</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Cost</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map(r => (
                        <TableRow key={r.name}>
                          <TableCell>{r.name}</TableCell>
                          <TableCell align="right">{r.days.size}</TableCell>
                          <TableCell align="right">{formatHours(r.courtMinutes)}</TableCell>
                          <TableCell align="right">{cost(r.courtMinutes)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {rows.reduce((n, r) => n + r.days.size, 0)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatHours(totalMinutes)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{cost(totalMinutes)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Breakdown per venue</Typography>
                  {rows.map(r => (
                    <Accordion key={r.name} disableGutters variant="outlined">
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                          <Typography>{r.name}</Typography>
                          <Typography color="text.secondary">
                            {r.bookings} booking{r.bookings !== 1 ? 's' : ''} · {formatHours(r.courtMinutes)} · {cost(r.courtMinutes)}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="caption" color="text.secondary">By time window</Typography>
                        <Table size="small" sx={{ mb: 2 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Time</TableCell>
                              <TableCell align="right">Courts</TableCell>
                              <TableCell align="right">Days</TableCell>
                              <TableCell align="right">Court time</TableCell>
                              <TableCell align="right">Cost</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {groupVenueItems(r.items).map(g => (
                              <TableRow key={`${g.startTime}-${g.endTime}-${g.courts}`}>
                                <TableCell>{g.startTime} - {g.endTime}</TableCell>
                                <TableCell align="right">{g.courts}</TableCell>
                                <TableCell align="right">{g.days}</TableCell>
                                <TableCell align="right">{formatHours(g.courtMinutes)}</TableCell>
                                <TableCell align="right">{cost(g.courtMinutes)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        <Typography variant="caption" color="text.secondary">By day</Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Time</TableCell>
                              <TableCell align="right">Courts</TableCell>
                              <TableCell align="right">Court time</TableCell>
                              <TableCell align="right">Cost</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {r.items.map((i, idx) => (
                              <TableRow key={`${i.date}-${i.startTime}-${idx}`}>
                                <TableCell>{format(parseISO(i.date), 'EEE, MMM d, yyyy')}</TableCell>
                                <TableCell>{i.startTime} - {i.endTime}</TableCell>
                                <TableCell align="right">{i.courts}</TableCell>
                                <TableCell align="right">{formatHours(i.courtMinutes)}</TableCell>
                                <TableCell align="right">{cost(i.courtMinutes)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
                <Alert severity="info">
                  Court time = slot duration × number of courts with a division assigned, so 2 courts for a 1.5-hour slot count as 3 hours.
                  Slots with no division assigned are not counted.
                  Leave the dates empty to include all bookings.
                </Alert>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCostDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Export PDF Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Export Court Schedule (PDF)</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Venue</InputLabel>
              <Select label="Venue" value={exportVenueId} onChange={(e) => setExportVenueId(e.target.value)}>
                <MenuItem value="">All venues</MenuItem>
                {getExportVenues().map(([id, name]) => (
                  <MenuItem key={id} value={id}>{name}</MenuItem>
                ))}
                {bookings.some(b => !b.venueId) && <MenuItem value="__none__">No venue set</MenuItem>}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="From"
                type="date"
                size="small"
                fullWidth
                value={exportFrom}
                onChange={(e) => setExportFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="To"
                type="date"
                size="small"
                fullWidth
                value={exportTo}
                onChange={(e) => setExportTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Alert severity="info">
              {(() => {
                const list = getExportBookings();
                const days = new Set(list.map(b => b.date)).size;
                return `${days} league day${days !== 1 ? 's' : ''} will be included. Leave dates empty to include everything.`;
              })()}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={handleExportPDF} disabled={getExportBookings().length === 0}>
            Export
          </Button>
        </DialogActions>
      </Dialog>

      {/* Repeat Assignments Dialog */}
      <Dialog open={!!repeatSourceDate} onClose={() => setRepeatSourceDate(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Copy / Duplicate Every…</DialogTitle>
        <DialogContent>
          {repeatSourceDate && (() => {
            const { targets, assignments: updates, structure, skipped } = planRepeat(bookings);
            const newCourts = structure.reduce((n: number, s: any) => n + s.courts.length, 0);
            return (
              <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Copy the court assignments of{' '}
                  <strong>{format(parseISO(repeatSourceDate.slice(0, 10)), 'EEEE, MMM dd, yyyy')}</strong>{' '}
                  to later league days.
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Repeat every</InputLabel>
                  <Select
                    label="Repeat every"
                    value={repeatEveryWeeks}
                    onChange={(e) => setRepeatEveryWeeks(Number(e.target.value))}
                  >
                    {[1, 2, 3, 4, 5, 6, 8].map(n => (
                      <MenuItem key={n} value={n}>{n === 1 ? 'week' : `${n} weeks`}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>For</InputLabel>
                  <Select
                    label="For"
                    value={repeatTimes}
                    onChange={(e) => setRepeatTimes(Number(e.target.value))}
                  >
                    <MenuItem value={0}>All future dates</MenuItem>
                    {[1, 2, 3, 4, 5, 6, 8, 10].map(n => (
                      <MenuItem key={n} value={n}>Next {n} occurrence{n > 1 ? 's' : ''}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    size="small"
                    checked={repeatOverwrite}
                    onChange={(e) => setRepeatOverwrite(e.target.checked)}
                    sx={{ pl: 0 }}
                  />
                  <Typography variant="body2">Overwrite slots that already have a division</Typography>
                </Box>
                <Alert severity={updates.length > 0 ? 'info' : 'warning'}>
                  {targets.length === 0
                    ? `No existing league days fall ${repeatEveryWeeks === 1 ? 'weekly' : `every ${repeatEveryWeeks} weeks`} after this date.`
                    : `${updates.length} slot${updates.length !== 1 ? 's' : ''} will be set across ${targets.length} date${targets.length !== 1 ? 's' : ''}` +
                      (newCourts > 0 ? `, booking ${newCourts} missing court slot${newCourts !== 1 ? 's' : ''} first` : '') +
                      (skipped > 0 ? ` (${skipped} already assigned, left unchanged).` : '.')}
                </Alert>
                {targets.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {targets.map(t => format(parseISO(t.slice(0, 10)), 'MMM dd')).join(', ')}
                  </Typography>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRepeatSourceDate(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRepeatAssignments}
            disabled={repeating || planRepeat(bookings).assignments.length === 0}
          >
            {repeating ? 'Copying…' : 'Copy'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Single Booking Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingBooking ? 'Edit Court Booking' : 'Add Court Booking'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />

            <VenueSelector value={venueId} onChange={setVenueId} fullWidth />
            {editingBooking && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                Changing the venue moves every booking on this date, since a league day is played in one venue.
              </Typography>
            )}

            <FormControl fullWidth required>
              <InputLabel>Courts Available</InputLabel>
              <Select
                multiple
                value={courtsAvailable}
                onChange={(e) => setCourtsAvailable(e.target.value as number[])}
                input={<OutlinedInput label="Courts Available" />}
                renderValue={(selected) => `Courts: ${selected.sort().join(', ')}`}
              >
                {AVAILABLE_COURTS.map((court) => (
                  <MenuItem key={court} value={court}>
                    <Checkbox checked={courtsAvailable.includes(court)} />
                    <ListItemText primary={`Court ${court}`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Start Time"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />

            <TextField
              label="End Time"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />

            <TextField
              label="Notes (optional)"
              multiline
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveBooking} variant="contained">
            {editingBooking ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Booking Dialog */}
      <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bulk Add Court Bookings</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <MultiDateCalendar
              value={bulkDates}
              onChange={setBulkDates}
              markedDates={bookings.map((b) => b.date)}
            />

            <VenueSelector value={bulkVenueId} onChange={setBulkVenueId} fullWidth />

            <FormControl fullWidth required>
              <InputLabel>Courts Available</InputLabel>
              <Select
                multiple
                value={bulkCourts}
                onChange={(e) => setBulkCourts(e.target.value as number[])}
                input={<OutlinedInput label="Courts Available" />}
                renderValue={(selected) => `Courts: ${selected.sort().join(', ')}`}
              >
                {AVAILABLE_COURTS.map((court) => (
                  <MenuItem key={court} value={court}>
                    <Checkbox checked={bulkCourts.includes(court)} />
                    <ListItemText primary={`Court ${court}`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Start Time"
              type="time"
              value={bulkStartTime}
              onChange={(e) => setBulkStartTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />

            <TextField
              label="End Time"
              type="time"
              value={bulkEndTime}
              onChange={(e) => setBulkEndTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />

            <TextField
              label="Notes (optional)"
              multiline
              rows={2}
              value={bulkNotes}
              onChange={(e) => setBulkNotes(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkCreate} variant="contained">
            Create All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Slot Assignment Dialog removed — assignment is now inline */}

      {/* Manage Bookings Dialog */}
      <Dialog open={manageBookingsDialogOpen} onClose={() => setManageBookingsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Bookings for {manageBookingsDateKey ? format(parseISO(manageBookingsDateKey), 'EEEE, MMMM dd, yyyy') : ''}
        </DialogTitle>
        <DialogContent dividers>
          {(groupedBookings[manageBookingsDateKey] || []).length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No bookings for this date.</Typography>
          ) : (
            (groupedBookings[manageBookingsDateKey] || []).map((booking, idx, arr) => (
              <Box
                key={booking.id}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                py={1.5}
                sx={{ borderBottom: idx < arr.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
              >
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {booking.startTime} – {booking.endTime}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Courts {booking.courtsAvailable.join(', ')}{booking.venueName ? ` · ${booking.venueName}` : ''}
                  </Typography>
                  {booking.notes && (
                    <Typography variant="caption" color="text.secondary">{booking.notes}</Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Edit this entry">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => { setManageBookingsDialogOpen(false); handleOpenDialog(booking); }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete this entry">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteBooking(booking.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageBookingsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CourtManagement;
